import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BulkActionBar from '../../components/ui/BulkActionBar';
import InvoiceTable from './components/InvoiceTable';
import InvoiceFilters from './components/InvoiceFilters';
import InvoiceStats from './components/InvoiceStats';
import InvoiceMobileCard from './components/InvoiceMobileCard';
import DebugModal from './components/DebugModal';
import Toast from '../../components/ui/Toast';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { user, currentOrgId } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: { from: '', to: '' },
    uploadDate: getLocalDateInputValue(),
    confidence: '',
    currency: '',
    vatType: '',
    amountRange: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [isLoading, setIsLoading] = useState(true);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const previewCacheRef = useRef({});

  // Fetch invoices from database
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!user?.id || !currentOrgId) return;

      setIsLoading(true);
      try {
        // Fetch invoices with document info
        const { data, error } = await supabase
          .from('parsed_invoices')
          .select(`
            *,
            documents!inner(
              file_name,
              uploaded_at,
              storage_path
            ),
            raw_extracted_data (
              data
            )
          `)
          .eq('org_id', currentOrgId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Transform data to match expected format
        const transformedInvoices = (data || []).map(inv => {
          const netAmount = parseFloat(inv.net_amount || 0);
          const vatAmount = parseFloat(inv.vat_amount || 0);
          const totalAmount = netAmount + vatAmount;
          const invoiceDate = inv.invoice_date ? new Date(inv.invoice_date) : null;
          const isValidDate = invoiceDate instanceof Date && !Number.isNaN(invoiceDate?.getTime());
          const manualEditMeta = inv.raw_extracted_data?.data?.manual_edit_meta;
          const isManualEdit = !!manualEditMeta;
          const confidenceValue = parseFloat(inv.confidence || 0);

          // Check lines vs totals mismatch
          const li = inv.raw_extracted_data?.data?.current?.line_items || [];
          const sumNet = li.reduce((s, x) => s + parseFloat(x?.net_amount || 0), 0);
          const sumVat = li.reduce((s, x) => s + parseFloat(x?.vat_amount || 0), 0);
          const sumGross = li.reduce((s, x) => s + parseFloat(x?.total || 0), 0);
          const dNet = Math.abs((parseFloat(inv.net_amount || 0)) - sumNet);
          const dVat = Math.abs((parseFloat(inv.vat_amount || 0)) - sumVat);
          const dGross = Math.abs((parseFloat(inv.gross_amount || 0)) - sumGross);
          const hasMismatch = dNet >= 0.01 || dVat >= 0.01 || dGross >= 0.01;

          // Tiered penalty (absolute delta in GBP)
          let penaltyCap = confidenceValue;
          if (hasMismatch) {
            const delta = Math.max(dNet, dVat, dGross);
            if (delta >= 5) penaltyCap = Math.min(penaltyCap, 0.25);
            else if (delta >= 1) penaltyCap = Math.min(penaltyCap, 0.4);
            else penaltyCap = Math.min(penaltyCap, 0.6);
          }

          const needsDateCap = !isValidDate;

          // Missing key fields / zero-amount guard
          const missingKeyFields = !inv.invoice_number || !inv.currency;
          const totalsZero = (totalAmount === 0) && (sumNet === 0 && sumVat === 0 && sumGross === 0);
          const linesAllZero = Array.isArray(li) && li.length > 0 && li.every(x =>
            (parseFloat(x?.unit_price || 0) === 0) &&
            (parseFloat(x?.net_amount || 0) === 0) &&
            (parseFloat(x?.total || 0) === 0)
          );

          let displayConfidence = confidenceValue;
          let confidenceCapReason = null;
          if (isManualEdit) {
            displayConfidence = 1;
            confidenceCapReason = 'manual_edit';
          } else if (hasMismatch) {
            displayConfidence = penaltyCap;
            confidenceCapReason = 'lines_totals_mismatch';
          } else if (needsDateCap) {
            displayConfidence = 0.25;
            confidenceCapReason = 'date_invalid_or_missing';
          }

          // Apply strong cap when there is missing invoice number/currency or all monetary values are zero
          if (missingKeyFields || totalsZero || linesAllZero) {
            displayConfidence = Math.min(displayConfidence, 0.2);
            confidenceCapReason = confidenceCapReason || 'missing_fields_or_zero_amounts';
          }

          return {
            id: inv.id,
            vendor: inv.vendor_name,
            invoiceNumber: inv.invoice_number,
            filename: inv.documents?.file_name,
            storagePath: inv.documents?.storage_path,
            date: inv.invoice_date,
            netAmount: netAmount,
            vatBreakdown: [
              { rate: 20, amount: vatAmount }
            ],
            totalAmount: totalAmount,
            currency: inv.currency || 'GBP',
            confidence: displayConfidence,
            rawConfidence: confidenceValue,
            status: inv.status || 'parsed',
            uploadedAt: inv.documents?.uploaded_at,
            reviewedAt: inv.updated_at,
            accountReference: inv.vendor_account_ref,
            paymentTerms: null,
            servicePeriod: null,
            confidenceCapReason,
            manualEditMeta,
            mismatchDelta: hasMismatch ? { net: dNet, vat: dVat, gross: dGross } : null
          }
        });

        const nonExportedInvoices = transformedInvoices?.filter(inv => inv?.status !== 'exported');

        setInvoices(nonExportedInvoices);
        setFilteredInvoices(nonExportedInvoices);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setInvoices([]);
        setFilteredInvoices([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [user?.id, currentOrgId]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...invoices];

    // Apply search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(invoice =>
        invoice?.vendor?.toLowerCase()?.includes(searchTerm) ||
        invoice?.invoiceNumber?.toLowerCase()?.includes(searchTerm) ||
        invoice?.filename?.toLowerCase()?.includes(searchTerm) ||
        invoice?.accountReference?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Apply status filter
    if (filters?.status) {
      filtered = filtered?.filter(invoice => invoice?.status === filters?.status);
    }

    // Apply date range filter
    if (filters?.dateRange?.from && filters?.dateRange?.to) {
      filtered = filtered?.filter(invoice => {
        const invoiceDate = new Date(invoice.date);
        const fromDate = new Date(filters.dateRange.from);
        const toDate = new Date(filters.dateRange.to);
        return invoiceDate >= fromDate && invoiceDate <= toDate;
      });
    }

    // Apply upload date filter
    if (filters?.uploadDate) {
      filtered = filtered?.filter(invoice => {
        const uploadedDate = formatLocalDate(invoice?.uploadedAt);
        return uploadedDate && uploadedDate === filters.uploadDate;
      });
    }

    // Apply confidence filter
    if (filters?.confidence) {
      const minConfidence = parseFloat(filters?.confidence);
      filtered = filtered?.filter(invoice => invoice?.confidence >= minConfidence);
    }

    // Apply currency filter
    if (filters?.currency) {
      filtered = filtered?.filter(invoice => invoice?.currency === filters?.currency);
    }

    // Apply VAT type filter
    if (filters?.vatType) {
      filtered = filtered?.filter(invoice => {
        const vatRates = invoice?.vatBreakdown?.map(vat => vat?.rate);
        switch (filters?.vatType) {
          case 'standard':
            return vatRates?.includes(20);
          case 'reduced':
            return vatRates?.includes(5);
          case 'zero':
            return vatRates?.includes(0);
          case 'mixed':
            return vatRates?.length > 1;
          case 'none':
            return invoice?.vatBreakdown?.length === 0;
          default:
            return true;
        }
      });
    }

    // Apply amount range filter
    if (filters?.amountRange) {
      filtered = filtered?.filter(invoice => {
        const amount = invoice?.totalAmount;
        switch (filters?.amountRange) {
          case '0-100':
            return amount < 100;
          case '100-500':
            return amount >= 100 && amount < 500;
          case '500-1000':
            return amount >= 500 && amount < 1000;
          case '1000-5000':
            return amount >= 1000 && amount < 5000;
          case '5000+':
            return amount >= 5000;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredInvoices(filtered);
  }, [invoices, filters, sortConfig]);

  // Calculate statistics
  const stats = {
    total: invoices?.length,
    reviewed: invoices?.filter(inv => inv?.status === 'reviewed' || inv?.status === 'exported')?.length,
    needsReview: invoices?.filter(inv => inv?.status === 'needs_review')?.length,
    exported: invoices?.filter(inv => inv?.status === 'exported')?.length,
    totalValue: invoices?.reduce((sum, inv) => sum + inv?.totalAmount, 0),
    avgConfidence: invoices?.length > 0 ? invoices?.reduce((sum, inv) => sum + inv?.confidence, 0) / invoices?.length : 0,
    currency: 'GBP'
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const openDebug = async (invoice) => {
    try {
      const { data, error } = await supabase
        .from('parsed_invoices')
        .select('*, raw_extracted_data(data)')
        .eq('id', invoice?.id)
        .single();
      if (error) throw error;
      setDebugData(data);
      setDebugOpen(true);
    } catch (e) {
      console.error('Failed to load debug data', e);
      alert('Failed to load debug data.');
    }
  };

  const resolveStoragePath = async (invoice) => {
    let storagePath = invoice?.storagePath;
    let fileName = invoice?.filename || 'invoice.pdf';
    if (!storagePath) {
      const { data: pi, error: piError } = await supabase
        .from('parsed_invoices')
        .select('documents(storage_path, file_name)')
        .eq('id', invoice?.id)
        .single();
      if (!piError) {
        storagePath = pi?.documents?.storage_path || storagePath;
        fileName = pi?.documents?.file_name || fileName;
      }
    }
    return { storagePath, fileName };
  };

  const handleDownload = async (invoice) => {
    try {
      const { storagePath, fileName } = await resolveStoragePath(invoice);

      if (!storagePath) {
        setToast({ open: true, message: 'No file available for this invoice.', type: 'error' });
        return;
      }
      const { data, error } = await supabase
        .storage
        .from('invoice-documents')
        .createSignedUrl(storagePath, 3600);
      if (error) throw error;
      const res = await fetch(data?.signedUrl);
      if (!res.ok) throw new Error('Failed to fetch file');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setToast({ open: true, message: `Downloaded ${fileName}`, type: 'success' });
    } catch (e) {
      console.error('Download failed:', e);
      setToast({ open: true, message: 'Failed to download PDF.', type: 'error' });
    }
  };

  const getPreviewUrl = async (invoice) => {
    const cached = previewCacheRef.current[invoice?.id];
    const now = Date.now();
    if (cached && (now - cached.timestamp) < 4 * 60 * 1000) {
      return cached.url;
    }
    const { storagePath } = await resolveStoragePath(invoice);
    if (!storagePath) throw new Error('No file available for preview.');
    const { data, error } = await supabase
      .storage
      .from('invoice-documents')
      .createSignedUrl(storagePath, 300);
    if (error) throw error;
    previewCacheRef.current[invoice?.id] = { url: data?.signedUrl, timestamp: now };
    return data?.signedUrl;
  };

  const handleSelectAll = () => {
    setSelectedInvoices(filteredInvoices?.map(inv => inv?.id));
  };

  const handleClearSelection = () => {
    setSelectedInvoices([]);
  };

  const describeInvoice = (invoice) => {
    if (invoice?.invoiceNumber) return `Invoice ${invoice.invoiceNumber}`;
    if (invoice?.filename) return invoice.filename;
    return `Invoice ${invoice?.id}`;
  };

  const handleBulkReview = async () => {
    try {
      const selectedRecords = invoices?.filter(inv => selectedInvoices?.includes(inv?.id));
      if (!selectedRecords?.length) {
        alert('Please select at least one invoice.');
        return;
      }

      const validationIssues = [];
      selectedRecords.forEach(inv => {
        const reasons = [];
        if (!inv?.vendor?.trim()) reasons.push('Vendor name is required');
        if (!inv?.invoiceNumber?.trim()) reasons.push('Invoice number is required');
        if (!inv?.date) reasons.push('Invoice date is required');
        if (!inv?.accountReference?.trim()) reasons.push('Vendor selection is required');
        if (reasons.length) {
          validationIssues.push({ invoice: inv, reasons });
        }
      });

      if (validationIssues.length > 0) {
        const lines = validationIssues.slice(0, 3).map(issue => {
          const title = describeInvoice(issue.invoice);
          return `${title}: ${issue.reasons.join(', ')}`;
        });
        if (validationIssues.length > 3) {
          lines.push(`+ ${validationIssues.length - 3} more invoices need attention.`);
        }
        alert(`Cannot mark as reviewed:\n${lines.join('\n')}`);
        return;
      }

      // Update status in database
      const { error } = await supabase
        .from('parsed_invoices')
        .update({
          status: 'reviewed',
          updated_at: new Date().toISOString()
        })
        .in('id', selectedInvoices);

      if (error) throw error;

      // Update local state
      setInvoices(prev => prev?.map(inv =>
        selectedInvoices?.includes(inv?.id)
          ? { ...inv, status: 'reviewed', reviewedAt: new Date()?.toISOString() }
          : inv
      ));
      setSelectedInvoices([]);
    } catch (error) {
      console.error('Error updating invoices:', error);
      alert('Failed to update invoices. Please try again.');
    }
  };

  const handleBulkUnreview = async () => {
    try {
      // Update status in database
      const { error } = await supabase
        .from('parsed_invoices')
        .update({
          status: 'needs_review'
        })
        .in('id', selectedInvoices);

      if (error) throw error;

      // Update local state
      setInvoices(prev => prev?.map(inv =>
        selectedInvoices?.includes(inv?.id)
          ? { ...inv, status: 'needs_review', reviewedAt: null }
          : inv
      ));
      setSelectedInvoices([]);
    } catch (error) {
      console.error('Error updating invoices:', error);
      alert('Failed to update invoices. Please try again.');
    }
  };

  const handleBulkExport = async () => {
    try {
      // Only export reviewed invoices
      const reviewedInvoices = filteredInvoices.filter(inv =>
        selectedInvoices.includes(inv.id) && inv.status === 'reviewed'
      );

      if (reviewedInvoices.length === 0) {
        alert('Please select at least one reviewed invoice to export.');
        return;
      }

      if (reviewedInvoices.length !== selectedInvoices.length) {
        const confirmed = confirm(
          `Only ${reviewedInvoices.length} of ${selectedInvoices.length} selected invoices are reviewed. Continue with export?`
        );
        if (!confirmed) return;
      }

      // Fetch full invoice details with line items from database
      const { data: fullInvoices, error: fetchError } = await supabase
        .from('parsed_invoices')
        .select(`
          *,
          raw_extracted_data(data)
        `)
        .in('id', reviewedInvoices.map(inv => inv.id));

      if (fetchError) throw fetchError;

      // Generate Sage 50 CSV format
      // Header: Type,Account Reference,Nominal A/C Ref,Department Code,Date,Reference,Details,Net Amount,Tax Code,Tax Amount
      const csvRows = [];

      // CSV Header
      csvRows.push([
        'Type',
        'Account Reference',
        'Nominal A/C Ref',
        'Department Code',
        'Date',
        'Reference',
        'Details',
        'Net Amount',
        'Tax Code',
        'Tax Amount'
      ]);

      // Process each invoice
      const mapVatToTaxCode = (rate, code) => {
        const r = typeof rate === 'number' ? rate : parseFloat(rate || 0) || 0;
        const c = (code || '').toString().toUpperCase();
        if (c === 'S' || r === 20) return 'T1';     // Standard 20%
        if (c === 'R' || r === 5)  return 'T5';     // Reduced 5%
        if (c === 'Z' || r === 0)  return 'T0';     // Zero 0%
        if (c === 'E')             return 'T2';     // Exempt
        if (c === 'O')             return 'T9';     // Outside scope
        // Fallback by numeric rate
        if (r === 20) return 'T1';
        if (r === 5)  return 'T5';
        if (r === 0)  return 'T0';
        return 'T9';
      };

      const fmtDateDDMMYYYY = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      };

      fullInvoices.forEach(invoice => {
        const lineItems = invoice.raw_extracted_data?.data?.line_items || [];
        const invoiceDate = fmtDateDDMMYYYY(invoice.invoice_date || '');
        const supplierRef = invoice.vendor_account_ref || 'SUPP001';
        const invoiceRef = invoice.invoice_number || '';

        if (lineItems.length === 0) {
          // If no line items, create single line with totals
          csvRows.push([
            'PI', // Purchase Invoice
            supplierRef,
            '5000', // Default nominal code (purchases)
            '0', // Department code
            invoiceDate,
            invoiceRef,
            invoice.vendor_name || 'Purchase Invoice',
            (invoice.net_amount || 0).toFixed(2),
            'T1', // Standard VAT rate
            (invoice.vat_amount || 0).toFixed(2)
          ]);
        } else {
          // Create a line for each item using per-line VAT
          lineItems.forEach(item => {
            const qty = parseFloat(item?.quantity || 0) || 0;
            const unit = parseFloat(item?.unit_price || 0) || 0;
            // Prefer recomputed net when qty & unit present for consistency
            const computedNet = qty * unit;
            const net = (qty > 0 && unit > 0)
              ? computedNet
              : (typeof item?.net_amount === 'number' ? item?.net_amount : computedNet);
            const rate = (typeof item?.vat_rate === 'number') ? item?.vat_rate : parseFloat(item?.vat_rate || 0) || 0;
            const vat = (typeof item?.vat_amount === 'number') ? item?.vat_amount : (net * rate / 100);
            const taxCode = mapVatToTaxCode(rate, item?.vat_code);

            csvRows.push([
              'PI',
              supplierRef,
              '5000',
              '0',
              invoiceDate,
              invoiceRef,
              item?.description || 'Purchase Item',
              (net || 0).toFixed(2),
              taxCode,
              (vat || 0).toFixed(2)
            ]);
          });
        }
      });

      // Convert to CSV string
      const csvContent = csvRows
        .map(row => row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
        .join('\n');

      // Trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `sage50_invoices_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Create export record
      const { error: exportError } = await supabase
        .from('exports')
        .insert({
          user_id: user.id,
          org_id: currentOrgId,
          format: 'csv',
          count: reviewedInvoices.length,
          filter_snapshot_json: { invoice_ids: reviewedInvoices.map(inv => inv.id) }
        });

      if (exportError) console.error('Error creating export record:', exportError);

      // Update invoice statuses to 'exported'
      const { error: updateError } = await supabase
        .from('parsed_invoices')
        .update({
          status: 'exported',
          updated_at: new Date().toISOString()
        })
        .in('id', reviewedInvoices.map(inv => inv.id));

      if (updateError) throw updateError;

      // Update local state: remove exported invoices from the working list immediately
      setInvoices(prev => prev.filter(inv => !reviewedInvoices.some(ri => ri.id === inv.id)));

      setSelectedInvoices([]);
      alert(`Successfully exported ${reviewedInvoices.length} invoice(s) to Sage 50 format!`);
    } catch (error) {
      console.error('Error exporting invoices:', error);
      alert('Failed to export invoices. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Gather target invoices with their document info before deletion
      const { data: targets, error: fetchTargetsErr } = await supabase
        .from('parsed_invoices')
        .select('id, document_id, documents(storage_path, file_name)')
        .in('id', selectedInvoices);
      if (fetchTargetsErr) throw fetchTargetsErr;

      // Delete invoices (cascades to raw_extracted_data via FK on raw_extracted_data.invoice_id)
      const { error: delErr } = await supabase
        .from('parsed_invoices')
        .delete()
        .in('id', selectedInvoices);
      if (delErr) throw delErr;

      // For each unique document, if no other invoices reference it, delete the storage file and the document row
      const docsMap = new Map();
      (targets || []).forEach(t => {
        if (!t?.document_id) return;
        if (!docsMap.has(t.document_id)) {
          docsMap.set(t.document_id, {
            id: t.document_id,
            storage_path: t?.documents?.storage_path || null,
            file_name: t?.documents?.file_name || 'invoice.pdf'
          });
        }
      });

      for (const doc of docsMap.values()) {
        // Check if any invoices still reference this document
        const { count, error: countErr } = await supabase
          .from('parsed_invoices')
          .select('id', { count: 'exact', head: true })
          .eq('document_id', doc.id);
        if (countErr) {
          console.error('Count check failed for document', doc.id, countErr);
          continue;
        }

        if ((count || 0) === 0) {
          // Remove storage file if present
          try {
            if (doc.storage_path) {
              const { error: rmErr } = await supabase
                .storage
                .from('invoice-documents')
                .remove([doc.storage_path]);
              if (rmErr) console.warn('Storage removal failed:', doc.storage_path, rmErr);
            }
          } catch (e) {
            console.warn('Storage removal exception:', e);
          }

          // Delete document row (removes hash/size metadata with it)
          const { error: delDocErr } = await supabase
            .from('documents')
            .delete()
            .eq('id', doc.id);
          if (delDocErr) console.warn('Document delete failed:', doc.id, delDocErr);
        }
      }

      // Update local state
      setInvoices(prev => prev?.filter(inv => !selectedInvoices?.includes(inv?.id)));
      setSelectedInvoices([]);
    } catch (error) {
      console.error('Error deleting invoices:', error);
      alert('Failed to delete invoices. Please try again.');
    }
  };

  const bulkActions = [
    {
      label: 'Mark as Reviewed',
      icon: 'CheckCircle',
      onClick: handleBulkReview,
      primary: true
    },
    {
      label: 'Mark as Unreviewed',
      icon: 'XCircle',
      onClick: handleBulkUnreview,
    },
    {
      label: 'Export to Sage',
      icon: 'Download',
      onClick: handleBulkExport
    },
    {
      label: 'Delete',
      icon: 'Trash2',
      onClick: handleBulkDelete,
      destructive: true,
      requiresConfirmation: true,
      confirmTitle: 'Delete Invoices',
      confirmMessage: 'Are you sure you want to delete the selected invoices? This action cannot be undone.'
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="flex items-center space-x-3">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="text-lg text-muted-foreground">Loading invoices...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Invoices</h1>
              <p className="text-muted-foreground mt-2">
                Review, edit, and manage your processed invoices
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="hidden md:flex items-center bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-smooth ${
                    viewMode === 'table' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Table" size={16} className="mr-2" />
                  Table
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-smooth ${
                    viewMode === 'cards' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Grid3X3" size={16} className="mr-2" />
                  Cards
                </button>
              </div>
              
              <Button
                variant="default"
                iconName="Upload"
                iconPosition="left"
                onClick={() => navigate('/uploads')}
              >
                Upload Invoices
              </Button>
            </div>
          </div>

          {/* Statistics */}
          <InvoiceStats stats={stats} className="mb-8" />

          {/* Filters */}
          <InvoiceFilters
            filters={filters}
            onFiltersChange={setFilters}
            resultCount={filteredInvoices?.length}
            className="mb-6"
          />

          {/* Bulk Actions */}
          <BulkActionBar
            selectedCount={selectedInvoices?.length}
            totalCount={filteredInvoices?.length}
            actions={bulkActions}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            position="sticky"
            className="mb-6"
          />

          {/* Invoice List */}
          {viewMode === 'table' ? (
            <InvoiceTable
              invoices={filteredInvoices}
              selectedInvoices={selectedInvoices}
              onSelectionChange={setSelectedInvoices}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              sortConfig={sortConfig}
              onSort={handleSort}
              onDebug={openDebug}
              onDownload={handleDownload}
              onPreviewRequest={getPreviewUrl}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInvoices?.map((invoice) => (
                <InvoiceMobileCard
                  key={invoice?.id}
                  invoice={invoice}
                  isSelected={selectedInvoices?.includes(invoice?.id)}
                  onSelectionChange={(invoiceId, checked) => {
                    if (checked) {
                      setSelectedInvoices(prev => [...prev, invoiceId]);
                    } else {
                      setSelectedInvoices(prev => prev?.filter(id => id !== invoiceId));
                    }
                  }}
                />
              ))}
              
              {filteredInvoices?.length === 0 && (
                <div className="col-span-full p-12 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="FileText" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No invoices found</h3>
                  <p className="text-muted-foreground mb-4">
                    Upload some invoices to get started or adjust your filters.
                  </p>
                  <Button
                    variant="outline"
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => navigate('/uploads')}
                  >
                    Upload Invoices
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {debugOpen && (
        <DebugModal isOpen={debugOpen} onClose={() => setDebugOpen(false)} data={debugData} />
      )}
      <Toast open={toast.open} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, open: false })} />
    </div>
  );
};

export default InvoicesPage;

function getLocalDateInputValue(date = new Date()) {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA');
}

function formatLocalDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA');
}
